import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { authenticationSsoGoogle, logout } from '@/store/slices/authentication';
import { resolveLoginRedirect } from '@/utils/login-redirect';

import {
  enabledProviders,
  providerClientId,
  readSsoConfig,
  ssoRedirectUri,
  type SsoProvider,
} from '../../config';
import { GOOGLE_POPUP_REDIRECT_URI, loadGis, requestGoogleCode } from '../../gis';
import { buildTelegramAuthorize } from '../../telegramStart';
import { writeSsoAttempt } from '../../pkceState';
import { ProviderButton, type ProviderButtonMode } from '../ProviderButton';
import { ProviderDivider } from '../ProviderDivider';

import { Buttons, Notice, Root } from './SocialLoginBar.styles';

export type SocialLoginBarProps = {
  /** Holds the post-auth destination the routing effect reads (mirrors the magic-link/password flow). */
  redirectTargetRef: React.MutableRefObject<string | null>;
  /** Called once a Google sign-in has fully settled (token + brands + session); opens the routing gate. */
  onResolved: () => void;
  /** Lets the host page show its full-screen loader while a provider flow is in flight. */
  onBusyChange?: (busy: boolean) => void;
  /** In-app route to restore after the Telegram round-trip. */
  returnTo?: string;
  /** Button copy context: 'signin' (default, /login) or 'signup' (/register). */
  mode?: ProviderButtonMode;
};

type NoticeKind = 'none' | 'linkSent' | 'error';

/**
 * The Google + Telegram sign-in bar shown under the login form. Reads the enabled providers from
 * `window.config` (renders nothing when none are enabled), and owns the per-provider flow:
 *  - Google: GIS popup code client -> `authenticationSsoGoogle` (no redirect; settles in place).
 *  - Telegram: full-page OIDC code-flow redirect (the /telegram-callback page finishes it).
 * A stale/existing account comes back as "login link sent to email" (shown as a notice, not an error).
 */
export const SocialLoginBar = ({
  redirectTargetRef,
  onResolved,
  onBusyChange,
  returnTo,
  mode = 'signin',
}: SocialLoginBarProps): React.ReactElement | null => {
  const dispatch = useAppDispatch();
  // The visitor's onboarding session, read BEFORE the sign-in logout tears it down (store + localStorage).
  // Captured at the top of each handler and passed to the backend so the configured brand binds to the
  // new account (see authentication.ts withSsoLoginOptions); it cannot be recovered post-logout.
  const onboardingSessionId = useAppSelector((state) => state.session.sessionId);
  const config = React.useMemo(readSsoConfig, []);
  const providers = React.useMemo(() => enabledProviders(config), [config]);
  const [active, setActive] = React.useState<SsoProvider | null>(null);
  const [notice, setNotice] = React.useState<NoticeKind>('none');

  // Preload the GIS script on mount (only when Google is offered) so the click-path `await loadGis()`
  // resolves as an already-settled microtask. Fetching it inside the click handler spends the browser's
  // transient-activation window on a cold cache, so the popup is blocked ('popup_failed_to_open') on the
  // first Google click. Failures are swallowed here; requestGoogleCode retries and surfaces the error.
  React.useEffect(() => {
    if (providers.includes('google')) {
      void loadGis().catch(() => undefined);
    }
  }, [providers]);

  const startGoogle = React.useCallback(async (): Promise<void> => {
    const clientId = providerClientId(config, 'google');
    if (!clientId) {
      return;
    }
    setNotice('none');
    // Only flip the button to loading; do NOT put the host into full-screen loading yet. The Google
    // popup settles in place, and the host swaps the whole form (incl. this bar) for a PageLoader while
    // busy, so an early onBusyChange(true) would unmount the bar before the linkSent/error notice below
    // could render (the outcome was silently lost). Stay mounted; the button's loading state is enough.
    setActive('google');
    // Capture the onboarding session BEFORE logout clears it, so the backend can bind the configured
    // brand to the account (logout wipes it from the store and localStorage).
    const boundSessionId = onboardingSessionId ?? undefined;
    try {
      // Drop any prior identity before minting a new one, preserving the visitor/marketing context
      // (mirrors the magic-link/password submit handlers).
      dispatch(logout({ keepVisitorContext: true }));
      const code = await requestGoogleCode(clientId);
      const result = await dispatch(
        authenticationSsoGoogle({ code, redirect_uri: GOOGLE_POPUP_REDIRECT_URI, session_id: boundSessionId }),
      ).unwrap();
      if (result.success) {
        redirectTargetRef.current = resolveLoginRedirect(result.data.redirect_url);
        // Point of no return: NOW hand the host its full-screen loader — the routing effect takes over,
        // so unmounting the bar is fine. We stay busy from here (no reset below).
        onBusyChange?.(true);
        onResolved();
        return;
      }
      // { linkSent: true } — a stale/existing account got a confirmation email instead of a login.
      setNotice('linkSent');
    } catch {
      setNotice('error');
    }
    setActive(null);
  }, [config, dispatch, onBusyChange, onboardingSessionId, onResolved, redirectTargetRef]);

  const startTelegram = React.useCallback(async (): Promise<void> => {
    const clientId = providerClientId(config, 'telegram');
    const redirectUri = ssoRedirectUri(config);
    if (!clientId || !redirectUri) {
      return;
    }
    setNotice('none');
    setActive('telegram');
    // Capture the onboarding session before logout clears it; it rides the stored attempt across the
    // full-page redirect so the callback can bind the configured brand (see buildTelegramAuthorize).
    const boundSessionId = onboardingSessionId ?? undefined;
    try {
      dispatch(logout({ keepVisitorContext: true }));
      const { url, attempt } = await buildTelegramAuthorize(clientId, redirectUri, returnTo, boundSessionId);
      writeSsoAttempt(attempt);
      // Point of no return: hand the host its loader immediately before the full-page navigation.
      // Doing it before the async build above would unmount the bar and swallow the catch's error
      // notice if buildTelegramAuthorize threw (e.g. crypto unavailable on an insecure origin).
      onBusyChange?.(true);
      window.location.assign(url);
      // Full-page navigation; nothing below runs.
    } catch {
      setNotice('error');
      setActive(null);
    }
  }, [config, dispatch, onBusyChange, onboardingSessionId, returnTo]);

  if (providers.length === 0) {
    return null;
  }

  return (
    <Root>
      <ProviderDivider />
      <Buttons>
        {providers.map((provider) => (
          <ProviderButton
            key={provider}
            provider={provider}
            state={active === provider ? 'loading' : active ? 'disabled' : 'idle'}
            mode={mode}
            onClick={provider === 'google' ? startGoogle : startTelegram}
          />
        ))}
      </Buttons>
      {notice === 'linkSent' && <Notice $error={false} text="Check your email to finish signing in." />}
      {notice === 'error' && <Notice $error text="Something went wrong. Please try again." />}
    </Root>
  );
};

export default SocialLoginBar;
